import { motion, useReducedMotion } from "framer-motion";
import React, { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_METEOR_COUNT = 5;

type MeteorConfig = {
  top: string;
  left: string;
  travelX: number;
  travelY: number;
  length: number;
  duration: number;
  repeatDelay: number;
  opacity: number;
};

const randomBetween = (min: number, max: number) =>
  min + Math.random() * (max - min);

const createRandomMeteor = (): MeteorConfig => ({
  top: `${randomBetween(2, 28)}%`,
  left: `${randomBetween(20, 98)}%`,
  travelX: -randomBetween(130, 280),
  travelY: randomBetween(90, 180),
  length: randomBetween(50, 115),
  duration: randomBetween(1.8, 2.8),
  repeatDelay: randomBetween(8, 16),
  opacity: randomBetween(0.55, 0.92),
});

interface MeteorStreakProps {
  staggerDelay: number;
}

const MeteorStreak: React.FC<MeteorStreakProps> = ({ staggerDelay }) => {
  const [config, setConfig] = useState(() => createRandomMeteor());
  const [run, setRun] = useState(0);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    },
    [],
  );

  const scheduleNext = useCallback(() => {
    timeoutRef.current = window.setTimeout(() => {
      setConfig(createRandomMeteor());
      setRun((n) => n + 1);
    }, config.repeatDelay * 1000);
  }, [config.repeatDelay]);

  const angle = (Math.atan2(config.travelY, config.travelX) * 180) / Math.PI;

  return (
    <motion.div
      key={run}
      className="absolute"
      style={{
        top: config.top,
        left: config.left,
        width: config.length,
        height: 2,
        rotate: angle,
        transformOrigin: "100% 50%",
        background: `linear-gradient(90deg, transparent 0%, rgba(199,210,254,${
          config.opacity * 0.35
        }) 55%, rgba(255,255,255,${config.opacity}) 100%)`,
        boxShadow: `0 0 8px rgba(165,180,252,${config.opacity * 0.5})`,
        borderRadius: 9999,
      }}
      initial={{ x: 0, y: 0, opacity: 0 }}
      animate={{
        x: [0, config.travelX],
        y: [0, config.travelY],
        opacity: [0, config.opacity, config.opacity * 0.85, 0],
      }}
      transition={{
        duration: config.duration,
        delay: run === 0 ? staggerDelay : 0,
        ease: "linear",
        times: [0, 0.08, 0.75, 1],
      }}
      onAnimationComplete={scheduleNext}
    />
  );
};

interface ShootingMeteorsProps {
  count?: number;
  className?: string;
}

const ShootingMeteors: React.FC<ShootingMeteorsProps> = ({
  count = DEFAULT_METEOR_COUNT,
  className = "",
}) => {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) return null;

  return (
    <div
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      aria-hidden
    >
      {Array.from({ length: count }, (_, i) => (
        <MeteorStreak key={i} staggerDelay={i * 2.5} />
      ))}
    </div>
  );
};

export default ShootingMeteors;
